import { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { OnboardingProgress } from "~/molecules/OnboardingProgress";
import { UploadStep } from "~/molecules/onboard-documents/UploadStep";
import { ConfigureStep } from "~/molecules/onboard-documents/ConfigureStep";
import { FieldsStep } from "~/molecules/onboard-documents/FieldsStep";
import { CompletionStep } from "~/molecules/onboard-documents/CompletionStep";

export type OnboardState = {
  files: File[];
  createdVersions: { id: number; documentId: number; fileType: string }[];
};

export type OnboardAction =
  | { type: "SET_FILES"; files: File[] }
  | { type: "SET_CREATED_VERSIONS"; versions: OnboardState["createdVersions"] }
  | { type: "RESET" };

function onboardReducer(
  state: OnboardState,
  action: OnboardAction,
): OnboardState {
  switch (action.type) {
    case "SET_FILES":
      return { ...state, files: action.files };
    case "SET_CREATED_VERSIONS":
      return { ...state, createdVersions: action.versions };
    case "RESET":
      return { files: [], createdVersions: [] };
    default:
      return state;
  }
}

const initialState: OnboardState = { files: [], createdVersions: [] };

export default function OnboardDocuments() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [state, dispatch] = useReducer(onboardReducer, initialState);

  const goToNext = () =>
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));

  const handleReset = () => {
    dispatch({ type: "RESET" });
    setCurrentStep(0);
  };

  const steps = [
    {
      title: t("library.onboardDocuments.steps.upload.title"),
      description: t("library.onboardDocuments.steps.upload.description"),
      component: (
        <UploadStep files={state.files} dispatch={dispatch} onNext={goToNext} />
      ),
    },
    {
      title: t("library.onboardDocuments.steps.configure.title"),
      description: t("library.onboardDocuments.steps.configure.description"),
      component: (
        <ConfigureStep
          files={state.files}
          dispatch={dispatch}
          onNext={goToNext}
        />
      ),
    },
    {
      title: t("library.onboardDocuments.steps.fields.title"),
      description: t("library.onboardDocuments.steps.fields.description"),
      component: <FieldsStep onNext={goToNext} />,
    },
    {
      title: null,
      description: null,
      component: <CompletionStep onReset={handleReset} />,
    },
  ];

  return (
    <div className="flex flex-col gap-8 py-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-3xl font-bold">
          {t("library.onboardDocuments.header")}
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          {t("library.onboardDocuments.subtitle")}
        </p>
      </div>
      {currentStep < steps.length - 1 && (
        <OnboardingProgress currentIndex={currentStep} steps={steps} />
      )}
      <div className="flex justify-center">
        <div className="max-w-xl">{steps[currentStep]?.component}</div>
      </div>
    </div>
  );
}
